import { ObjectId } from '@racemap/sdk/schema/base';
import type { EventTypes } from '@racemap/sdk/schema/events';
import type { ShowValues } from '@racemap/utilities/api-client';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Select, Space, Spin, Typography } from 'antd';
import { type FC, useState } from 'react';
import { useEvent } from 'src/lib/customHooks';
import { useEventsPaginated } from 'src/lib/customHooks/useEventsPaginated';
import { useDebounceCallback } from 'usehooks-ts';
import { EventLogo } from './BasicComponents/EventLogo';
import { EventTimes } from './BasicComponents/EventTimes';

type SearchEventProps = {
  value?: ObjectId | null;
  onChange?: (value: ObjectId | null, option?: OptionType | null) => void;
  filter: string;
  show?: Array<ShowValues>;
  eventTypes?: Array<EventTypes>;
  ignoreList?: Array<string>;
  placeholder?: string;
  onSelect?: (event: RacemapEvent | null) => void;
};

const { Text } = Typography;

type OptionType = {
  label: string;
  value: string;
  event: RacemapEvent;
};

/**
 * A searchable dropdown component for selecting events with pagination support.
 *
 * @param filter - The filter to apply when fetching events (default: 'can-read')
 * @param show - Array of event visibility options to include (default: ['hidden', 'atomiceventsonly'])
 * @param ignoreList - Array of event IDs to exclude from the search results (default: [])
 * @param placeholder - Placeholder text for the search input (default: 'Search for events...')
 * @param onSelect - Callback function called when an event is selected, receives the selected event object or null
 *
 * @returns A Select component that allows users to search and select events with:
 * - Real-time search functionality
 * - Infinite scroll pagination (loads more results when scrolling near the end)
 * - Event logos displayed in dropdown options
 * - Loading states and debounced API calls
 * - Filtered results based on ignore list
 */
export const SearchEvent: FC<SearchEventProps> = ({
  value = null,
  onChange,
  filter = 'can-read',
  show = ['hidden', 'atomiceventsonly'],
  eventTypes,
  ignoreList = [] as Array<string>,
  placeholder = 'Search for events...',
  onSelect,
}) => {
  const [search, setSearch] = useState('');
  const { event } = useEvent({ eventId: value ?? undefined });
  const { data, isLoading, loadNextPage, hasNextPage } = useEventsPaginated({
    filter,
    show,
    eventTypes,
    searchValue: search,
    pageSize: 10,
  });
  // debounce loadNextPage to avoid multiple calls
  const debouncedLoadNextPage = useDebounceCallback(() => {
    loadNextPage();
  }, 100);

  const options = data
    // filter duplicates
    .filter(filterDuplicates)
    .filter((event) => !ignoreList.includes(event.id))
    .map((event) => ({
      label: event.name || event.id,
      value: event.id,
      event,
    }));

  const handleSelect = (value: OptionType | null) => {
    if (value == null) return;

    const selectedEvent = data.find((event) => event.id === value.value) || null;
    if (onSelect) {
      onSelect(selectedEvent);
    }
  };

  const handleChange = (value: OptionType | null) => {
    if (onChange == null) return;

    if (value == null) {
      onChange(null, null);
    } else {
      onChange(new ObjectId(value.value), value);
    }
  };

  const selectedValue =
    value != null && event && event.id === value.toString()
      ? { value: event.id, label: event.name || event.id, event }
      : null;

  return (
    <Select<OptionType>
      labelInValue
      showSearch
      value={selectedValue}
      onChange={handleChange}
      searchValue={search}
      filterOption={false}
      onSearch={setSearch}
      notFoundContent={isLoading ? <Spin size="small" /> : 'No results found'}
      options={options}
      onSelect={handleSelect}
      autoClearSearchValue
      placeholder={placeholder}
      listHeight={240}
      listItemHeight={59.5}
      loading={isLoading}
      optionRender={(option, { index }) => {
        if (hasNextPage && index > options.length - 5) {
          // load next page when we are close to the end
          debouncedLoadNextPage();
        }

        return (
          <Space style={{ height: 48, width: 500 }}>
            <EventLogo eventId={option.data.id} size={42} />
            <Space direction="vertical" style={{ width: '100%' }} size={1}>
              <Text strong>{option.label}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <EventTimes event={option.data.event} />
              </Text>
            </Space>
          </Space>
        );
      }}
      popupMatchSelectWidth={false}
      style={{ minWidth: 300 }}
    />
  );
};

const filterDuplicates = (
  event: RacemapEvent,
  index: number,
  self: Array<RacemapEvent>,
): boolean => {
  return index === self.findIndex((e) => e.id === event.id);
};
